import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Heart, Shield, Users, FileText, Lock, DollarSign, MessageCircle,
  CheckCircle, ArrowRight, ChevronDown, ChevronUp, Sparkles, Star,
  Clock, Award, Zap, Eye, BookOpen, HelpCircle, Phone, Mail,
  Target, Lightbulb, TrendingUp, UserCheck, Globe, Layers
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { AppLogoStatic } from '../components/AppLogo';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// FAQ Component
const FAQItem = ({ question, answer, isOpen, onToggle }) => (
  <motion.div 
    className="border-b border-[#EBF0F5] last:border-0"
    initial={false}
  >
    <button
      onClick={onToggle}
      className="w-full py-5 flex items-center justify-between text-left hover:text-[#2B4C7E] transition-colors"
    >
      <span className="font-semibold text-gray-800 pr-4">{question}</span>
      <motion.div
        animate={{ rotate: isOpen ? 180 : 0 }}
        transition={{ duration: 0.2 }}
        className="flex-shrink-0"
      >
        <ChevronDown className="h-5 w-5 text-[#2B4C7E]" />
      </motion.div>
    </button>
    <motion.div
      initial={false}
      animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
      transition={{ duration: 0.3 }}
      className="overflow-hidden"
    >
      <p className="pb-5 text-gray-600 leading-relaxed">{answer}</p>
    </motion.div>
  </motion.div>
);

// Process Step Component
const ProcessStep = ({ number, title, description, icon: Icon, isLast }) => (
  <motion.div 
    variants={fadeInUp}
    className="relative flex gap-4"
  >
    {/* Connector Line */}
    {!isLast && (
      <div className="absolute left-6 top-14 w-0.5 h-[calc(100%-3.5rem)] bg-gradient-to-b from-[#B9C7D6] to-[#DCC39A]" />
    )}
    
    {/* Step Number */}
    <div className="relative z-10 flex-shrink-0">
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#2B4C7E] to-[#C8A97E] flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-[#2B4C7E]/30">
        {number}
      </div>
    </div>
    
    {/* Content */}
    <div className="flex-1 pb-8">
      <div className="flex items-center gap-3 mb-2">
        <Icon className="h-5 w-5 text-[#2B4C7E]" />
        <h4 className="text-lg font-bold text-gray-800">{title}</h4>
      </div>
      <p className="text-gray-600 leading-relaxed">{description}</p>
    </div>
  </motion.div>
);

// Benefit Card Component
const BenefitCard = ({ icon: Icon, title, description, gradient }) => (
  <motion.div variants={fadeInUp}>
    <Card className={`p-6 h-full rounded-2xl border-0 bg-gradient-to-br ${gradient} shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1`}>
      <div className="w-14 h-14 rounded-xl bg-white/90 flex items-center justify-center mb-4 shadow-md">
        <Icon className="h-7 w-7 text-[#2B4C7E]" />
      </div>
      <h4 className="text-lg font-bold text-gray-800 mb-2">{title}</h4>
      <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
    </Card>
  </motion.div>
);

// Feature Highlight Component
const FeatureHighlight = ({ icon: Icon, title, items }) => (
  <motion.div variants={fadeInUp} className="bg-white rounded-2xl p-6 shadow-lg border border-[#EBF0F5]">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2B4C7E] to-[#C8A97E] flex items-center justify-center">
        <Icon className="h-5 w-5 text-white" />
      </div>
      <h4 className="text-lg font-bold text-gray-800">{title}</h4>
    </div>
    <ul className="space-y-3">
      {items.map((item, index) => (
        <li key={index} className="flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
          <span className="text-gray-600 text-sm">{item}</span>
        </li>
      ))}
    </ul>
  </motion.div>
);

export default function ServiceOverview() {
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqs = [
    {
      question: "What happens to my information after I pass away?",
      answer: "Your designated primary beneficiary (executor) can initiate the 'Easy Passing' process, which securely releases your messages, documents, and funds to your chosen beneficiaries according to your instructions. All data remains encrypted and protected until this process is triggered."
    },
    {
      question: "How secure is my sensitive information?",
      answer: "We employ bank-level encryption (AES-256) for all sensitive data, including passwords and financial information. Your credentials vault is encrypted with a unique key derived from your account, ensuring only authorized beneficiaries can access it after the Easy Passing process is initiated."
    },
    {
      question: "Can I update my beneficiaries and messages anytime?",
      answer: "Yes, you have complete control over your account. You can add, remove, or modify beneficiaries, update your messages (Whispers), change fund allocations, and edit all documents at any time. Your legacy plan evolves with your life."
    },
    {
      question: "What types of messages can I leave for loved ones?",
      answer: "Easy Passing supports multiple message formats called 'Whispers': text messages, voice recordings, photos, and videos. You can address each message to specific beneficiaries, ensuring personalized communication with each loved one."
    },
    {
      question: "How do beneficiaries receive their designated funds?",
      answer: "Funds are distributed according to your specified allocations when the Easy Passing process is triggered. Beneficiaries receive notifications and can withdraw their designated amounts through secure payment methods including bank transfer and PayPal."
    },
    {
      question: "Is there a verification process for the Easy Passing trigger?",
      answer: "Yes, only your designated primary beneficiary (executor) can initiate the Easy Passing process. We recommend having open communication with your executor about their responsibilities. Secondary executors can act if the primary is unavailable."
    },
    {
      question: "What documents should I include in my legacy plan?",
      answer: "We recommend uploading your Last Will & Testament, insurance policies (life, home, auto), important account credentials, funeral preferences, and any special instructions for your belongings. You can also note the physical location of original documents."
    },
    {
      question: "Can I include my social media account information?",
      answer: "Yes, the Credentials Vault allows you to securely store login information for social media accounts, email accounts, streaming services, and any other digital accounts you want your beneficiaries to have access to or manage after your passing."
    }
  ];

  const processSteps = [
    {
      icon: UserCheck,
      title: "Create Your Account",
      description: "Sign up with email or Google authentication. Set up your profile with a bio and profile picture that will be shared with your beneficiaries."
    },
    {
      icon: Users,
      title: "Designate Your Beneficiaries",
      description: "Add the people who matter most. Assign primary and secondary executors who can initiate the Easy Passing process. Each beneficiary receives an invitation to accept their role."
    },
    {
      icon: MessageCircle,
      title: "Create Your Whispers",
      description: "Record heartfelt messages in text, voice, photo, or video format. Address each Whisper to specific beneficiaries for personalized communication that transcends time."
    },
    {
      icon: FileText,
      title: "Upload Essential Documents",
      description: "Store your Last Will & Testament, insurance policies, and note the physical location of important documents. Your beneficiaries will know exactly where to find everything."
    },
    {
      icon: Lock,
      title: "Secure Your Credentials",
      description: "Add login information for digital accounts, passwords, and access codes to the encrypted Credentials Vault. Only released to beneficiaries upon Easy Passing."
    },
    {
      icon: DollarSign,
      title: "Allocate Your Funds",
      description: "Deposit and distribute funds among your beneficiaries. Specify exact amounts or percentages for each person. Funds are securely held until distribution."
    },
    {
      icon: Heart,
      title: "Live With Peace of Mind",
      description: "Your legacy plan is complete. Update it anytime as life changes. When the time comes, your executor initiates Easy Passing, and your wishes are honored."
    }
  ];

  const benefits = [
    {
      icon: Heart,
      title: "Preserve Meaningful Connections",
      description: "Leave lasting messages that keep your memory alive and provide comfort to loved ones during difficult times.",
      gradient: "from-[#F7F1E8] to-[#F7F1E8]"
    },
    {
      icon: Shield,
      title: "Bank-Level Security",
      description: "Your sensitive information is protected with AES-256 encryption, the same standard used by financial institutions.",
      gradient: "from-[#EBF0F5] to-[#EBF0F5]"
    },
    {
      icon: Clock,
      title: "Reduce Family Burden",
      description: "Eliminate confusion and stress for your family by organizing everything they need in one secure location.",
      gradient: "from-[#EBF0F5] to-[#EBF0F5]"
    },
    {
      icon: Zap,
      title: "Instant Access When Needed",
      description: "Your beneficiaries gain immediate access to critical information exactly when they need it most.",
      gradient: "from-[#F7F1E8] to-[#F7F1E8]"
    },
    {
      icon: Eye,
      title: "Complete Control & Privacy",
      description: "You decide who sees what. Customize access levels and keep your information private until the right moment.",
      gradient: "from-green-50 to-emerald-50"
    },
    {
      icon: TrendingUp,
      title: "Evolving Legacy Plan",
      description: "Life changes, and so can your legacy plan. Update beneficiaries, messages, and allocations anytime.",
      gradient: "from-[#EBF0F5] to-[#EBF0F5]"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-[#EBF0F5]/30 to-[#F7F1E8]/30">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-[#EBF0F5]">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <AppLogoStatic size="md" showText={false} />
              <span className="text-xl font-bold bg-gradient-to-r from-[#2B4C7E] via-[#B3956B] to-[#C8A97E] bg-clip-text text-transparent">
                Easy Passing
              </span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-gray-600 hover:text-[#2B4C7E] transition-colors hidden sm:block">
                Home
              </Link>
              <Link to="/login">
                <Button className="bg-gradient-to-r from-[#2B4C7E] to-[#B3956B] text-white rounded-full hover:from-[#1E3A63] hover:to-[#9C7F55]">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 md:py-24" aria-labelledby="main-heading">
        <div className="absolute inset-0 bg-gradient-to-br from-[#EBF0F5]/50 via-[#F2E9D9]/30 to-[#EBF0F5]/50" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#B9C7D6]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#DCC39A]/20 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4">
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 bg-[#EBF0F5] rounded-full text-[#1E3A63] text-sm font-medium mb-6">
              <BookOpen className="h-4 w-4" />
              Complete Guide to Digital Legacy Preservation
            </motion.div>
            
            <motion.h1 id="main-heading" variants={fadeInUp} className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Legacy Preservation Services by{' '}
              <span className="bg-gradient-to-r from-[#2B4C7E] via-[#B3956B] to-[#C8A97E] bg-clip-text text-transparent">
                Easy Passing
              </span>
            </motion.h1>
            
            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed">
              Preserve your legacy with Easy Passing. Our legacy preservation services help you 
              plan, protect, and share your memories with loved ones now and for future generations.
            </motion.p>
            
            <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4">
              <Link to="/lead" aria-label="Lead capture for legacy preservation services">
                <Button size="lg" className="bg-gradient-to-r from-[#2B4C7E] to-[#B3956B] text-white rounded-full px-8 hover:from-[#1E3A63] hover:to-[#9C7F55] shadow-lg shadow-[#2B4C7E]/30">
                  Get Started with Easy Passing Today <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="#what-is-legacy-preservation">
                <Button size="lg" variant="outline" className="rounded-full px-8 border-[#B9C7D6] text-[#1E3A63] hover:bg-[#EBF0F5]">
                  Learn More <ChevronDown className="ml-2 h-5 w-5" />
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* What is Legacy Preservation Section */}
      <section id="what-is-legacy-preservation" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid lg:grid-cols-2 gap-12 items-center"
          >
            <motion.div variants={fadeInUp}>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#EBF0F5] rounded-full text-[#1E3A63] text-sm font-medium mb-4">
                <Lightbulb className="h-4 w-4" />
                Definition & Overview
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                What is Digital Legacy Preservation?
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  <strong className="text-gray-800">Digital legacy preservation</strong> is the comprehensive process of 
                  organizing, securing, and preparing your digital assets, personal messages, and important documents 
                  for transfer to designated beneficiaries after your passing.
                </p>
                <p>
                  In today's digital age, our lives extend far beyond physical possessions. We hold memories in photos, 
                  maintain relationships through messages, manage finances online, and store critical information across 
                  numerous digital platforms. Legacy preservation ensures none of this is lost.
                </p>
                <p>
                  <strong className="text-gray-800">Easy Passing</strong> provides a modern, secure platform that combines 
                  traditional estate planning principles with digital-first solutions, making it simple to organize your 
                  final wishes and ensure your legacy is preserved exactly as you intend.
                </p>
              </div>
              
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="bg-[#EBF0F5] rounded-xl p-4">
                  <div className="text-3xl font-bold text-[#2B4C7E] mb-1">85%</div>
                  <div className="text-sm text-gray-600">of Americans don't have a will or legacy plan</div>
                </div>
                <div className="bg-[#F7F1E8] rounded-xl p-4">
                  <div className="text-3xl font-bold text-[#B3956B] mb-1">$1.5T+</div>
                  <div className="text-sm text-gray-600">in digital assets go unaccounted for annually</div>
                </div>
              </div>
            </motion.div>
            
            <motion.div variants={fadeInUp} className="relative">
              <div className="bg-gradient-to-br from-[#EBF0F5] to-[#F2E9D9] rounded-3xl p-8 shadow-xl">
                <h3 className="text-xl font-bold text-gray-800 mb-6">What Easy Passing Preserves</h3>
                <div className="space-y-4">
                  {[
                    { icon: MessageCircle, label: "Personal Messages & Memories", desc: "Text, voice, photo, and video messages for loved ones" },
                    { icon: FileText, label: "Legal Documents", desc: "Wills, insurance policies, and important paperwork" },
                    { icon: Lock, label: "Digital Credentials", desc: "Passwords, account access, and security information" },
                    { icon: DollarSign, label: "Financial Assets", desc: "Funds designated for specific beneficiaries" },
                    { icon: Users, label: "Beneficiary Instructions", desc: "Clear guidance on who receives what" },
                    { icon: Heart, label: "Final Wishes", desc: "Funeral preferences and special requests" }
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-4 bg-white/80 rounded-xl p-4">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#2B4C7E] to-[#C8A97E] flex items-center justify-center flex-shrink-0">
                        <item.icon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800">{item.label}</h4>
                        <p className="text-sm text-gray-600">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* How Easy Passing Works - Process Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#EBF0F5]/50 to-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-3 py-1 bg-[#EBF0F5] rounded-full text-[#1E3A63] text-sm font-medium mb-4">
              <Layers className="h-4 w-4" />
              Step-by-Step Process
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How Easy Passing Works
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our streamlined 7-step process makes legacy planning simple, secure, and comprehensive. 
              Complete at your own pace and update anytime.
            </motion.p>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="max-w-3xl mx-auto"
          >
            {processSteps.map((step, index) => (
              <ProcessStep
                key={index}
                number={index + 1}
                title={step.title}
                description={step.description}
                icon={step.icon}
                isLast={index === processSteps.length - 1}
              />
            ))}
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 rounded-full text-green-700 text-sm font-medium mb-4">
              <Award className="h-4 w-4" />
              Key Advantages
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Benefits of Digital Legacy Planning
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover why thousands of individuals trust Easy Passing to preserve their legacy 
              and protect their loved ones' future.
            </motion.p>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {benefits.map((benefit, index) => (
              <BenefitCard key={index} {...benefit} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* Eligibility Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-[#EBF0F5]/50">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <div className="text-center mb-12">
              <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-3 py-1 bg-[#EBF0F5] rounded-full text-[#1E3A63] text-sm font-medium mb-4">
                <Target className="h-4 w-4" />
                Who Should Use Easy Passing
              </motion.div>
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Eligibility & Ideal Users
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-gray-600 max-w-2xl mx-auto">
                Easy Passing is designed for anyone who wants to ensure their digital legacy 
                is preserved and their loved ones are cared for.
              </motion.p>
            </div>

            <motion.div 
              variants={staggerContainer}
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              <FeatureHighlight
                icon={Users}
                title="Parents & Families"
                items={[
                  "Leave messages for children to read at milestones",
                  "Ensure spouse has access to all accounts",
                  "Organize childcare instructions",
                  "Store family history and memories"
                ]}
              />
              <FeatureHighlight
                icon={Globe}
                title="Business Owners"
                items={[
                  "Document business credentials",
                  "Outline succession plans",
                  "Store partnership agreements",
                  "Preserve client relationships"
                ]}
              />
              <FeatureHighlight
                icon={Heart}
                title="Individuals"
                items={[
                  "Record personal messages for loved ones",
                  "Organize digital accounts",
                  "Store important documents",
                  "Express final wishes"
                ]}
              />
              <FeatureHighlight
                icon={Shield}
                title="Estate Planners"
                items={[
                  "Complement legal documents",
                  "Provide detailed instructions",
                  "Ensure nothing is overlooked",
                  "Reduce family disputes"
                ]}
              />
            </motion.div>

            <motion.div variants={fadeInUp} className="mt-12 bg-gradient-to-r from-[#2B4C7E] to-[#B3956B] rounded-2xl p-8 text-white text-center">
              <h3 className="text-2xl font-bold mb-3">No Age Restrictions, No Prerequisites</h3>
              <p className="text-[#EBF0F5] max-w-2xl mx-auto mb-6">
                Whether you're 25 or 85, single or married, with or without children — if you have a digital 
                footprint and people you care about, Easy Passing is for you. There's no "too early" when it 
                comes to planning your legacy.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="bg-white/20 rounded-full px-4 py-2 text-sm">✓ Adults 18+</div>
                <div className="bg-white/20 rounded-full px-4 py-2 text-sm">✓ Any Country</div>
                <div className="bg-white/20 rounded-full px-4 py-2 text-sm">✓ Free to Start</div>
                <div className="bg-white/20 rounded-full px-4 py-2 text-sm">✓ No Legal Requirements</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Outcomes Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <div className="text-center mb-12">
              <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 rounded-full text-emerald-700 text-sm font-medium mb-4">
                <TrendingUp className="h-4 w-4" />
                Expected Results
              </motion.div>
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Outcomes & What to Expect
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-gray-600 max-w-2xl mx-auto">
                When you create your legacy plan with Easy Passing, here's what you and your beneficiaries can expect.
              </motion.p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* For You */}
              <motion.div variants={fadeInUp}>
                <Card className="h-full p-8 rounded-2xl border-2 border-[#D7E0EA] bg-gradient-to-br from-[#EBF0F5] to-white">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#2B4C7E] to-[#2B4C7E] flex items-center justify-center">
                      <Sparkles className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">For You (Account Holder)</h3>
                  </div>
                  <ul className="space-y-4">
                    {[
                      "Peace of mind knowing your affairs are in order",
                      "Confidence that your messages will reach loved ones",
                      "Assurance that your final wishes will be honored",
                      "Reduced anxiety about leaving loose ends",
                      "A living document that evolves with your life",
                      "Secure storage for your most important information"
                    ].map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-[#2B4C7E] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </motion.div>

              {/* For Beneficiaries */}
              <motion.div variants={fadeInUp}>
                <Card className="h-full p-8 rounded-2xl border-2 border-[#EAD9BF] bg-gradient-to-br from-[#F7F1E8] to-white">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#C8A97E] to-[#C8A97E] flex items-center justify-center">
                      <Heart className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">For Your Beneficiaries</h3>
                  </div>
                  <ul className="space-y-4">
                    {[
                      "Immediate access to important information when needed",
                      "Heartfelt messages that provide comfort and closure",
                      "Clear instructions eliminating confusion",
                      "Direct access to funds without probate delays",
                      "Knowledge of your final wishes and preferences",
                      "A lasting connection to your memory"
                    ].map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-[#C8A97E] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#EBF0F5]/50 to-white">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <div className="text-center mb-12">
              <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-3 py-1 bg-[#F2E9D9] rounded-full text-[#9C7F55] text-sm font-medium mb-4">
                <HelpCircle className="h-4 w-4" />
                Common Questions
              </motion.div>
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-gray-600">
                Everything you need to know about digital legacy preservation and Easy Passing.
              </motion.p>
            </div>

            <motion.div variants={fadeInUp}>
              <Card className="rounded-2xl border-[#EBF0F5] overflow-hidden">
                <div className="p-6 md:p-8">
                  {faqs.map((faq, index) => (
                    <FAQItem
                      key={index}
                      question={faq.question}
                      answer={faq.answer}
                      isOpen={openFAQ === index}
                      onToggle={() => setOpenFAQ(openFAQ === index ? null : index)}
                    />
                  ))}
                </div>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#2B4C7E] via-[#B3956B] to-[#C8A97E] relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgY3g9IjIwIiBjeT0iMjAiIHI9IjIiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIi8+PC9nPjwvc3ZnPg==')] opacity-30" />
        
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full text-white text-sm font-medium mb-6">
              <Star className="h-4 w-4" />
              Start Your Legacy Today
            </motion.div>
            
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Preserve Your Legacy?
            </motion.h2>
            
            <motion.p variants={fadeInUp} className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
              Join thousands of individuals who have taken the important step of organizing their 
              digital legacy. Start free today — no credit card required.
            </motion.p>
            
            <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4">
              <Link to="/login">
                <Button size="lg" className="bg-white text-[#2B4C7E] hover:bg-[#EBF0F5] rounded-full px-8 font-semibold shadow-lg">
                  Create Free Account <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>
            
            <motion.div variants={fadeInUp} className="mt-8 flex flex-wrap justify-center gap-6 text-white/80 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Free to start
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                No credit card needed
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Bank-level security
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <AppLogoStatic size="md" showText={false} />
              <span className="text-xl font-bold">Easy Passing</span>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6 text-gray-400 text-sm">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
              <Link to="/login" className="hover:text-white transition-colors">Sign In</Link>
              <a href="#what-is-legacy-preservation" className="hover:text-white transition-colors">Learn More</a>
            </div>
            
            <div className="text-gray-400 text-sm">
              © {new Date().getFullYear()} Easy Passing. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
